export type TodoAssignee = 'manager' | 'liver'

export interface EditableTodo {
  task: string
  assignee: TodoAssignee
  confidence?: number
  evidence?: string
  completed: boolean
  due_date: string | null
}

export type TodoConfidenceTone = 'high' | 'medium' | 'low' | 'unknown'

export interface TodoDisplayMeta {
  assigneeLabel: string
  completionLabel: string
  confidenceLabel: string
  confidenceTone: TodoConfidenceTone
  dueDateLabel: string
  evidenceLabel: string
}

export function normalizeTodos(todos: Array<Partial<EditableTodo>> | null | undefined): EditableTodo[] {
  return (todos ?? []).flatMap((todo) => {
    const task = typeof todo.task === 'string' ? todo.task.trim() : ''
    if (!task) return []
    const assignee = todo.assignee === 'liver' ? 'liver' : 'manager'
    const confidence =
      typeof todo.confidence === 'number'
        ? Math.max(0, Math.min(1, todo.confidence))
        : undefined
    const evidence = typeof todo.evidence === 'string' ? todo.evidence.trim() : ''
    const dueDate =
      typeof todo.due_date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(todo.due_date)
        ? todo.due_date
        : null
    return [{
      task,
      assignee,
      ...(confidence != null ? { confidence } : {}),
      ...(evidence ? { evidence } : {}),
      completed: todo.completed === true,
      due_date: dueDate
    }]
  })
}

export function buildTodoDisplayMeta(todo: EditableTodo): TodoDisplayMeta {
  return {
    assigneeLabel: todo.assignee === 'manager' ? 'マネージャー' : 'ライバー',
    completionLabel: todo.completed ? '完了' : '未完了',
    confidenceLabel:
      typeof todo.confidence === 'number'
        ? `信頼度 ${Math.round(todo.confidence * 100)}%`
        : '信頼度未設定',
    confidenceTone: confidenceTone(todo.confidence),
    dueDateLabel: todo.due_date ? `期限 ${todo.due_date}` : '期限未設定',
    evidenceLabel: todo.evidence ? '根拠あり' : '根拠なし'
  }
}

export function updateTodoAt(
  todos: EditableTodo[],
  index: number,
  patch: Partial<EditableTodo>
): EditableTodo[] {
  return todos.map((todo, i) => {
    if (i !== index) return { ...todo }
    return normalizeTodos([{ ...todo, ...patch }])[0] ?? { ...todo }
  })
}

export function toggleTodoAt(todos: EditableTodo[], index: number): EditableTodo[] {
  return updateTodoAt(todos, index, { completed: !todos[index]?.completed })
}

function confidenceTone(confidence: number | undefined): TodoConfidenceTone {
  if (typeof confidence !== 'number') return 'unknown'
  if (confidence >= 0.75) return 'high'
  if (confidence >= 0.45) return 'medium'
  return 'low'
}
